import {
  Box,
  Button,
  Divider,
  Flex,
  Group,
  Indicator,
  LoadingOverlay,
  Stack,
  Text,
  TextInput,
} from "@mantine/core";
import TextEditor from "../../../../components/ExternalEditors/TextEditor";
import { useEffect, useMemo, useRef, useState } from "react";
import { useConfigStore } from "../../../../stores/ConfigStore";
import { getFields } from "../../../../api/ContactAPI";
import { useLanguage } from "../../../../stores/LanguageStore";
import {
  createLinkedinInvitation,
  createLinkedinMessage,
  createSMS,
  generateLinkedinInvitation,
  generateLinkedinMessage,
  getLinkedinInvitations,
  getLinkedinMessages,
  getSMS,
} from "../../../../api/ModelsApi";
import { notifications } from "@mantine/notifications";
import MessageModalPreview from "../../../../components/View/MessageModalPreview";
import FolderSelector from "../../../../components/Selectors/FolderSelector";
import { useRouteLoaderData } from "react-router-dom";
import useNavigationBlocker from "../../../../components/View/NavigationBlocker";
import { useUIStore } from "../../../../stores/UIStore";
import BackButton from "../../../../components/View/BackButton";
import Generator from "../emails/Generator";
import { modals } from "@mantine/modals";
import FilesModal from "../../files/FilesModal";
import FileItemMessage from "./FileItemMessage";
import Sparkles from "../../../../components/Sparkles/Sparkles";
import SmsSenderField from "../../../../components/View/SmsSenderField";

const CreateModelLinkedin = ({
  handleClose,
  type,
  onCreated,
  maxChars = 280,
  folderProfile,
}) => {
  const editorRef = useRef();
  const lang = useLanguage((s) => s.language);
  const datafields = useConfigStore((s) => s.datafields);
  const datafieldTokens = useMemo(() => {
    let df = datafields.map((x) => ({
      label: x.name,
      value: x.identifier_placeholder,
    }));

    return df;
  }, [datafields]);
  const [dirty, setDirty] = useState(false);
  const [folder, setFolder] = useState(folderProfile || null);
  const [file, setFile] = useState(null);

  useEffect(() => {
    getFields();
  }, []);

  const handlePreview = ({ setText, setSender, open }) => {
    const fd = new FormData(document.getElementById("model-form"));
    const data = Object.fromEntries(fd);

    setText(editorRef.current.getContent({ format: "text" }));
    setSender(data?.sender);
    open();
  };

  const [submitting, setSubmitting] = useState(false);
  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    const fd = new FormData(e.target);
    const data = Object.fromEntries(fd);

    let text = editorRef.current.getContent({ format: "text" });

    let func;
    if (type === "linkedin_message") {
      func = createLinkedinMessage;
    }
    if (type === "linkedin_invitation") {
      func = createLinkedinInvitation;
    }
    if (type === "sms") {
      func = createSMS;
    }
    return func({
      name: data.name,
      text,
      sender: data?.sender,
      file_id: file?.id,
    })
      .then((response) => {
        let newId = response.data.model_id;
        setSubmitting(true);

        let funcGetter;
        if (type === "linkedin_message") {
          funcGetter = getLinkedinMessages;
        }
        if (type === "linkedin_invitation") {
          funcGetter = getLinkedinInvitations;
        }
        if (type === "sms") {
          funcGetter = getSMS;
        }

        funcGetter()
          .then((res) => {
            let newItemsList = res.data.models_list;
            let newItem = newItemsList.find((x) => x.id === newId);
            setDirty(false);
            notifications.show({
              title: lang.models.created,
              message: lang.models.created_text,
              color: "tertiary.8",
            });
            setTimeout(() => {
              if (onCreated) {
                onCreated(newItem);
              }
              handleClose?.();
            }, 100);
          })
          .finally(() => {
            setSubmitting(false);
          });
        if (folderProfile) {
          useUIStore.getState().setAsideLoading(true);
        }
      })
      .catch(() => setSubmitting(false));
  };

  const onGenerated = (html) => {
    if (dirty) {
      return modals.openConfirmModal({
        title: lang.models.generator.confirm.title,
        children: <Text size="sm">{lang.models.generator.confirm.text}</Text>,
        labels: {
          confirm: lang.models.generator.confirm.replace,
          cancel: lang.global.cancel,
        },
        onCancel: () => {},
        onConfirm: () => {
          editorRef.current?.setContent(html);
        },
        centered: true,
      });
    }

    editorRef.current?.setContent(html);
  };

  const handleFileAdd = (content) => {
    setFile(content.item);
  };

  useNavigationBlocker(dirty);

  return (
    <>
      <LoadingOverlay
        visible={submitting}
        overlayProps={{ blur: 1 }}
        zIndex={1001}
      />

      <Stack
        component={"form"}
        onSubmit={onSubmit}
        gap={0}
        id="model-form"
        onChange={() => setDirty(true)}
        h={"100%"}
        flex={1}
      >
        <Group wrap="nowrap" mb={"sm"}>
          <TextInput
            label={lang.model_emails.create.dialog.name}
            placeholder={lang.model_emails.create.dialog.name}
            name="name"
            w="50%"
            required
            data-autofocus
            autoFocus
            size="md"
          />
          {type === "sms" && (
            <SmsSenderField
              name="sender"
              label={lang.components.email_preview.sender}
              placeholder={lang.components.email_preview.sender}
              size="md"
              w="50%"
              required={false}
            />
          )}
          {folderProfile && (
            <Group w={400}>
              <FolderSelector
                value={folder}
                setValue={setFolder}
                type={"Model"}
              />
            </Group>
          )}
        </Group>

        {type === "linkedin_invitation" && (
          <Text mt={"xs"} c={"red"} size="md" mb={"md"}>
            {lang.model_invitation_linkedin.create.dialog.info_limit}
          </Text>
        )}
        {type === "sms" && (
          <Text mt={"xs"} c={"red"} size="md" mb={"md"}>
            {lang.model_invitation_linkedin.create.dialog.info_limit_sms}
          </Text>
        )}
        <TextEditor
          editorRef={editorRef}
          placeholder={lang.model_emails.create.dialog.placeholder}
          tokens={datafieldTokens}
          // noTop
          toolbar={"undo redo | tokens |"}
          maxChars={maxChars}
          onKeyUp={() => setDirty(true)}
          longVariableButton
        >
          <Group
            wrap="nowrap"
            style={{
              position: "absolute",
              top: 9,
              left: 136,
              zIndex: 4,
            }}
          >
            <Generator
              handler={
                type === "linkedin_message"
                  ? generateLinkedinMessage
                  : generateLinkedinInvitation
              }
              isLinkedin
              onFinish={onGenerated}
              isLinkedinInvitation={type === "linkedin_invitation"}
            />
          </Group>
        </TextEditor>

        {file && <FileItemMessage file={file} setFile={setFile} />}

        <Group justify="space-between" mt={"sm"}>
          <Flex gap={"lg"}>
            {type === "linkedin_message" && (
              <>
                <Box>
                  <FilesModal
                    isNew
                    isLinkedin
                    handleFileAdd={handleFileAdd}
                    isLongButton
                    buttonProps={{ size: "md", h: "100%", mr: "md" }}
                  />
                </Box>

                {/* <Box>
                  <FilesModal
                    isNew
                    isLinkedin
                    handleFileAdd={handleFileAdd}
                    isLongButton
                    buttonProps={{ size: "md", h: "100%", mr: "md" }}
                    defaultType={"audio"}
                  />
                </Box> */}
              </>
            )}
            <MessageModalPreview
              getData={handlePreview}
              type={type}
              size="md"
            />
          </Flex>
          <Flex gap={"md"}>
            <BackButton onClick={handleClose} size="md" />
            <Button type="submit" size="md">
              {lang.model_emails.create.dialog.submit_button}
            </Button>
          </Flex>
        </Group>
      </Stack>
    </>
  );
};

export default CreateModelLinkedin;
